<script>
  import { appIconUrl as sharedAppIconUrl } from "lib/util/appIcons.js";
  import { onDestroy, onMount, tick } from "svelte";
  import {
    DataTableSkeleton,
  } from "carbon-components-svelte";
  import Header from "lib/components/Header.svelte";
  import { SectionWrapper, Select } from "paxray-components-svelte";
  import SearchNew from "lib/components/searchNew.svelte";
  import SoftwareCatalogTable from "./components/SoftwareCatalogTable.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import { uiFeatureStore } from "@stores/uiFeatureStore.js";
  import { ROUTES } from "@utils/routes.js";

  const headers = [
    { key: "application", value: "App" },
    { key: "category", value: "Category" },
    { key: "type", value: "Type" },
    { key: "users", value: "User Share" },
    { key: "organization", value: "User Groups" },
    { key: "dependency_count", value: "Dependencies" },
    { key: "active_time_seconds", value: "Total Time Share" },
    { key: "copy_time_seconds", value: "Copy Time Share" },
    { key: "text_time_seconds", value: "Text Time Share" },
    { key: "clicks", value: "Click Share" },
  ];
  const softwareCatalogHelpContext = {
    task: "Analyze observed software usage, team coverage, dependencies, and usage hotspots.",
    starterQuestions: [
      "What should I look at first?",
      "Which apps dominate usage?",
      "Where is copy & paste high?",
      "Which dependencies stand out?",
    ],
  };

  let catalog = [];
  let projects = [];
  let appnameMappingByClearname = {};
  let summary = emptySummary();
  let selectedProject = "";
  let selectedCategory = "";
  let selectedType = "";
  let selectedTeam = "";
  let search = "";
  let loading = true;
  let error = "";
  // The on-going catalog remains in the codebase but is temporarily not exposed.
  const catalogMode = 0;
  let page = 1;
  let pageSize = 25;
  let paxbotVisible = false;
  let paxbotInput = "";
  let paxbotLanguage = "en";
  let paxbotLoading = false;
  let paxbotMessagesElement;
  let paxbotScrollSignature = "";
  let paxbotTyping = false;
  let paxbotTypingTimer;

  const paxbotLanguages = [
    { key: "en", label: "EN", name: "English" },
    { key: "de", label: "DE", name: "Deutsch" },
    { key: "es", label: "ES", name: "Español" },
    { key: "fr", label: "FR", name: "Français" },
    { key: "it", label: "IT", name: "Italiano" },
  ];
  const paxbotLanguageText = {
    en: {
      hello:
        "Hi, I'm Pax AI. Ask me about the current Software Catalog, application usage, annualized time, or dependencies.",
      starterQuestions: [
        "What should I look at first?",
        "Which apps dominate usage?",
        "Where is copy & paste high?",
        "Which dependencies stand out?",
      ],
      disclaimer: "Pax AI can make mistakes. Check important results.",
      thinking: "Pax AI is thinking...",
      replying: "Pax AI is replying...",
      placeholder: "Ask Pax AI...",
    },
    de: {
      hello:
        "Hi, ich bin Pax AI. Frag mich zum aktuellen Software Catalog, zur App-Nutzung, zu annualisierten Zeiten oder Abhängigkeiten.",
      starterQuestions: [
        "Was soll ich zuerst prüfen?",
        "Welche Apps dominieren?",
        "Wo gibt es viel Copy & Paste?",
        "Welche Abhängigkeiten fallen auf?",
      ],
      disclaimer: "Pax AI kann Fehler machen. Prüfe wichtige Ergebnisse.",
      thinking: "Pax AI denkt nach...",
      replying: "Pax AI antwortet...",
      placeholder: "Pax AI fragen...",
    },
    es: {
      hello:
        "Hola, soy Pax AI. Pregúntame sobre el catálogo de software, el uso de aplicaciones, los tiempos anualizados o las dependencias.",
      starterQuestions: [
        "¿Qué debería revisar primero?",
        "¿Qué apps dominan el uso?",
        "¿Dónde hay mucho copiar y pegar?",
        "¿Qué dependencias destacan?",
      ],
      disclaimer: "Pax AI puede cometer errores. Revisa los resultados importantes.",
      thinking: "Pax AI está pensando...",
      replying: "Pax AI está respondiendo...",
      placeholder: "Pregunta a Pax AI...",
    },
    fr: {
      hello:
        "Bonjour, je suis Pax AI. Interrogez-moi sur le catalogue logiciel, l'usage des applications, les temps annualisés ou les dépendances.",
      starterQuestions: [
        "Que dois-je examiner d'abord ?",
        "Quelles apps dominent l'usage ?",
        "Où le copier-coller est-il élevé ?",
        "Quelles dépendances ressortent ?",
      ],
      disclaimer: "Pax AI peut faire des erreurs. Vérifiez les résultats importants.",
      thinking: "Pax AI réfléchit...",
      replying: "Pax AI répond...",
      placeholder: "Demander à Pax AI...",
    },
    it: {
      hello:
        "Ciao, sono Pax AI. Chiedimi del catalogo software, dell'uso delle applicazioni, dei tempi annualizzati o delle dipendenze.",
      starterQuestions: [
        "Cosa dovrei controllare prima?",
        "Quali app dominano l'utilizzo?",
        "Dove c'è molto copia e incolla?",
        "Quali dipendenze emergono?",
      ],
      disclaimer: "Pax AI può commettere errori. Verifica i risultati importanti.",
      thinking: "Pax AI sta pensando...",
      replying: "Pax AI sta rispondendo...",
      placeholder: "Chiedi a Pax AI...",
    },
  };
  let paxbotChatMessages = [
    { role: "assistant", text: paxbotLanguageText.en.hello },
  ];

  $: PAXBOT_ENABLED = $uiFeatureStore.processExplorer.aiBot;
  $: paxbotLocale = paxbotLanguageText[paxbotLanguage] || paxbotLanguageText.en;
  $: categories = uniqueValues(catalog.map((item) => item.category));
  $: types = uniqueValues(catalog.map((item) => item.type));
  $: teams = uniqueValues(catalog.flatMap((item) => item.teams || []));
  $: filteredApplications = catalog.filter((application) => {
    const query = search.trim().toLowerCase();
    return (
      (!query || application.application.toLowerCase().includes(query)) &&
      (!selectedCategory || application.category === selectedCategory) &&
      (!selectedType || application.type === selectedType) &&
      (!selectedTeam || application.teams?.includes(selectedTeam))
    );
  });
  $: if (page > Math.max(1, Math.ceil(filteredApplications.length / pageSize))) {
    page = 1;
  }
  $: tableHeaders = headers.map((header) => {
    if (header.key === "application") {
      return {
        ...header,
        value: `Apps (${new Set(filteredApplications.map((item) => item.application)).size})`,
      };
    }
    if (header.key === "category") {
      return {
        ...header,
        value: `Categories (${new Set(filteredApplications.map((item) => item.category).filter(Boolean)).size})`,
      };
    }
    return header;
  });
  $: topApplicationShare = summary.active_time_seconds
    ? Math.round(
        ((catalog[0]?.active_time_seconds || 0) /
          summary.active_time_seconds) *
          100,
      )
    : 0;
  $: averageApplications = Number(
    summary.average_apps_per_user || 0,
  ).toFixed(1);
  $: topDependencies = buildTopDependencies(catalog);
  $: {
    const lastPaxbotMessage = paxbotChatMessages[paxbotChatMessages.length - 1];
    const nextPaxbotScrollSignature = [
      paxbotVisible,
      paxbotLoading,
      paxbotChatMessages.length,
      lastPaxbotMessage?.role || "",
      lastPaxbotMessage?.text || "",
    ].join("|");
    if (
      paxbotVisible &&
      nextPaxbotScrollSignature !== paxbotScrollSignature
    ) {
      paxbotScrollSignature = nextPaxbotScrollSignature;
      scrollPaxbotToBottom();
    }
  }

  onMount(async () => {
    await Promise.all([loadProjects(), loadAppnameMappings()]);
    await loadCatalog();
  });
  onDestroy(clearPaxbotTyping);

  function emptySummary() {
    return {
      application_count: 0,
      source_project_count: 0,
      users: 0,
      application_user_assignments: 0,
      average_apps_per_user: 0,
      active_time_seconds: 0,
      copy_time_seconds: 0,
      text_time_seconds: 0,
      clicks: 0,
      refresh_required: false,
      changes: {},
    };
  }

  function uniqueValues(values) {
    return [...new Set(values.filter(Boolean))].sort((a, b) =>
      a.localeCompare(b),
    );
  }

  async function loadProjects() {
    try {
      const response = await fetchWithSession("/api/monitoring/projects");
      if (response.ok) projects = await response.json();
    } catch (loadError) {
      error = loadError.message;
    }
  }

  async function loadAppnameMappings() {
    try {
      const response = await fetchWithSession("/api/damprojects/getAppnameMapping");
      if (!response.ok) return;
      const mappings = await response.json();
      appnameMappingByClearname = mappings.reduce((result, mapping) => {
        const technicalName = mapping.applicationname;
        const names = [mapping.clearname, technicalName]
          .map(normalizeApplicationName)
          .filter(Boolean);
        if (technicalName) {
          names.forEach((name) => {
            result[name] = technicalName;
          });
        }
        return result;
      }, {});
    } catch (loadError) {
      appnameMappingByClearname = {};
    }
  }

  async function loadCatalog() {
    loading = true;
    error = "";
    page = 1;
    try {
      const query = selectedProject
        ? `?project_id=${encodeURIComponent(selectedProject)}`
        : "";
      const response = await fetchWithSession(`/api/monitoring/catalog${query}`);
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.message || "Software catalog could not be loaded.");
      }
      catalog = (data.applications || []).map((application, index) => ({
        ...application,
        type: normalizeType(application.type),
        id: `${application.application}-${index}`,
        organization: [
          ...new Set([
            ...(application.departments || []),
            ...(application.teams || []),
          ]),
        ].join(", "),
      }));
      summary = data.summary || emptySummary();
      selectedCategory = "";
      selectedType = "";
      selectedTeam = "";
    } catch (loadError) {
      error = loadError.message || "Software catalog could not be loaded.";
    } finally {
      loading = false;
    }
  }

  function resetPage() {
    page = 1;
  }

  function formatDuration(seconds) {
    const hours = Number(seconds || 0) / 3600;
    if (hours >= 1000) {
      return `${new Intl.NumberFormat("en", {
        maximumFractionDigits: 1,
      }).format(hours / 1000)}k h`;
    }
    return `${new Intl.NumberFormat("en", {
      maximumFractionDigits: hours < 10 ? 1 : 0,
    }).format(hours)} h`;
  }

  function formatNumber(value) {
    return new Intl.NumberFormat("en", { notation: "compact" }).format(
      Number(value || 0),
    );
  }

  function formatType(value) {
    return String(normalizeType(value) || "Unclassified")
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  function formatCategory(value) {
    return value === "Weitere Anwendungen" ? "Others" : value;
  }

  function appIconUrl(application, fallbackApplication = "") {
    return sharedAppIconUrl(application, { clearname: fallbackApplication });
  }

  function technicalApplicationName(application) {
    const mappedTechnicalName = getMappedTechnicalName(application);
    return (
      mappedTechnicalName ||
      application?.technicalname ||
      application?.technical_name ||
      application?.applicationname ||
      application?.application ||
      ""
    );
  }

  function getMappedTechnicalName(application) {
    const candidates = [application?.clearname, application?.application]
      .map(normalizeApplicationName)
      .filter(Boolean);
    const exactMatch = candidates
      .map((name) => appnameMappingByClearname[name])
      .find(Boolean);
    if (exactMatch) return exactMatch;

    const partialMatch = Object.entries(appnameMappingByClearname).find(
      ([mappingName]) => {
        const compactMappingName = mappingName.replace(/[^a-z0-9]/g, "");
        return candidates.some((candidate) => {
          const compactCandidate = candidate.replace(/[^a-z0-9]/g, "");
          return (
            compactCandidate.includes(compactMappingName) ||
            compactMappingName.includes(compactCandidate)
          );
        });
      },
    );
    return partialMatch?.[1] || "";
  }

  function normalizeApplicationName(value) {
    return String(value || "").trim().toLowerCase();
  }

  function iconLookupName(application) {
    return technicalApplicationName(application)
      .split(/[\\/]/)
      .pop()
      .replace(/\.(exe|dll)$/i, "")
      .trim()
      .toLowerCase();
  }

  function catalogIconUrl(row) {
    const name = `${row.application || ""} ${iconLookupName(row)}`.toLowerCase();
    const localIcon = [
      ["outlook", "/img/assistant/apps/outlook.svg"],
      ["excel", "/img/assistant/apps/excel.svg"],
      ["sap", "/img/assistant/apps/sap.svg"],
      ["chrome", "/img/programIcons/chrome.svg"],
      ["edge", "/img/assistant/apps/edge.svg"],
      ["explorer", "/img/programIcons/explorer.svg"],
      ["word", "/img/programIcons/winword.svg"],
      ["acrobat", "/img/assistant/apps/acrobat.svg"],
      ["powerpoint", "/img/programIcons/powerpnt.svg"],
      ["firefox", "/img/assistant/apps/firefox.svg"],
      ["snipping", "/img/programIcons/snippingtool.svg"],
    ].find(([keyword]) => name.includes(keyword));

    if (localIcon) return localIcon[1];

    const technicalName = iconLookupName(row);
    return appIconUrl(technicalName, row.application);
  }

  function normalizeType(value) {
    const normalizedType = String(value || "").trim().toLowerCase();
    return normalizedType === "supportive" ? "support" : normalizedType;
  }

  function buildTopDependencies(applications) {
    const pairs = new Map();
    applications.forEach((application) => {
      (application.dependencies || []).forEach((dependency) => {
        const names = [application.application, dependency.application].sort(
          (a, b) => a.localeCompare(b),
        );
        const key = names.map((name) => name.toLowerCase()).join("\u001f");
        const current = pairs.get(key);
        if (
          !current ||
          dependency.transitions > current.transitions
        ) {
          pairs.set(key, {
            source: names[0],
            target: names[1],
            transitions: dependency.transitions,
          });
        }
      });
    });
    return [...pairs.values()]
      .sort((a, b) => b.transitions - a.transitions)
      .slice(0, 5);
  }

  function getPaxbotContext() {
    const projectName = selectedProject
      ? projects.find((project) => project.id === selectedProject)?.name
      : "All Projects & Teams";
    const firstVisibleItem = (page - 1) * pageSize;
    const visibleApplications = filteredApplications
      .slice(firstVisibleItem, firstVisibleItem + pageSize)
      .map((application) => ({
        application: application.application,
        category: formatCategory(application.category),
        type: formatType(application.type),
        annualActiveTime: formatDuration(application.active_time_seconds),
        users: application.users,
        departments: (application.departments || []).slice(0, 6),
        teams: (application.teams || []).slice(0, 6),
        dependencyCount: application.dependency_count,
        annualCopyTime: formatDuration(application.copy_time_seconds),
        annualTextTime: formatDuration(application.text_time_seconds),
        clicks: application.clicks,
      }));

    return {
      page: "software catalog",
      currentScope: {
        project: projectName,
        filters: {
          search: search || "All applications",
          category: selectedCategory || "All categories",
          type: selectedType ? formatType(selectedType) : "All types",
          team: selectedTeam || "All teams",
        },
        page,
        pageSize,
        matchingApplications: filteredApplications.length,
        sort: "Annual active time descending",
      },
      summary: {
        applications: summary.application_count,
        sourceProjects: summary.source_project_count,
        users: summary.users,
        applicationUserAssignments: summary.application_user_assignments,
        averageApplicationsPerUser: Number(
          summary.average_apps_per_user || 0,
        ).toFixed(1),
        annualActiveTime: formatDuration(summary.active_time_seconds),
        annualCopyTime: formatDuration(summary.copy_time_seconds),
        annualTextTime: formatDuration(summary.text_time_seconds),
        clicks: formatNumber(summary.clicks),
        topApplicationShare: `${topApplicationShare}%`,
      },
      visibleApplications,
      topDependencies: topDependencies.map((dependency) => ({
        source: dependency.source,
        target: dependency.target,
        transitions: dependency.transitions,
      })),
      definitions: {
        annualTime:
          "Annual active, copy and text time are extrapolated aggregate values after inactivity cleanup.",
        users: "Distinct users in the selected catalog scope.",
        averageApplicationsPerUser:
          "Distinct application-user assignments divided by distinct users.",
        dependencies:
          "Observed transitions between applications; they show connection strength, not causality.",
      },
    };
  }

  async function scrollPaxbotToBottom() {
    await tick();
    if (!paxbotMessagesElement) return;
    paxbotMessagesElement.scrollTop = paxbotMessagesElement.scrollHeight;
  }

  function clearPaxbotTyping() {
    if (paxbotTypingTimer) {
      clearTimeout(paxbotTypingTimer);
      paxbotTypingTimer = null;
    }
    paxbotTyping = false;
  }

  function animatePaxbotAnswer(answer) {
    clearPaxbotTyping();
    const fullText = String(answer || "").trim();
    const typingId = `paxbot-${Date.now()}-${Math.random()}`;
    let visibleLength = 0;
    paxbotTyping = true;
    paxbotChatMessages = [
      ...paxbotChatMessages,
      { role: "assistant", text: "", typingId },
    ];

    const typeNext = () => {
      visibleLength = Math.min(
        visibleLength + (fullText.length > 180 ? 3 : 2),
        fullText.length,
      );
      paxbotChatMessages = paxbotChatMessages.map((message) =>
        message.typingId === typingId
          ? { ...message, text: fullText.slice(0, visibleLength) }
          : message,
      );
      if (visibleLength < fullText.length) {
        paxbotTypingTimer = setTimeout(typeNext, 14);
      } else {
        paxbotTypingTimer = null;
        paxbotTyping = false;
      }
    };

    typeNext();
  }

  async function handlePaxbotQuestion(questionText = paxbotInput) {
    const question = String(questionText || "").trim();
    if (!question || paxbotLoading || paxbotTyping) return;

    const history = paxbotChatMessages;
    paxbotChatMessages = [
      ...paxbotChatMessages,
      { role: "user", text: question },
    ];
    paxbotInput = "";
    paxbotLoading = true;

    try {
      const response = await fetchWithSession("/api/damprojects/paxbot/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          question,
          history,
          context: {
            ...getPaxbotContext(),
            responseLanguage:
              paxbotLanguages.find(
                (language) => language.key === paxbotLanguage,
              )?.name || "English",
          },
        }),
      });
      const responseText = await response.text();
      let result = {};
      try {
        result = responseText ? JSON.parse(responseText) : {};
      } catch (parseError) {
        throw new Error(
          `Paxbot endpoint returned ${response.status || "an invalid response"}`,
        );
      }
      if (!response.ok || result.status === "error") {
        throw new Error(result.message || "Paxbot request failed");
      }
      paxbotLoading = false;
      animatePaxbotAnswer(result.answer);
    } catch (requestError) {
      clearPaxbotTyping();
      paxbotChatMessages = [
        ...paxbotChatMessages,
        {
          role: "assistant",
          text: `Pax AI could not retrieve an answer right now: ${requestError.message}`,
        },
      ];
    } finally {
      paxbotLoading = false;
    }
  }

  function handleStarterQuestion(question) {
    paxbotInput = question;
    handlePaxbotQuestion(question);
  }

  function handlePaxbotLanguageChange() {
    if (
      paxbotChatMessages.length === 1 &&
      paxbotChatMessages[0]?.role === "assistant"
    ) {
      paxbotChatMessages = [{ role: "assistant", text: paxbotLocale.hello }];
    }
  }

  function resetPaxbotChat() {
    clearPaxbotTyping();
    paxbotLoading = false;
    paxbotInput = "";
    paxbotChatMessages = [{ role: "assistant", text: paxbotLocale.hello }];
  }

  function handlePaxbotKeydown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handlePaxbotQuestion();
    }
  }
</script>

<Header
  section="Digital Twin"
  projectNameOverride="Digital Twin"
  projectHref={ROUTES.dam.root}
  area="Software Intelligence"
  areaClickable={false}
  breadcrumb="Software Catalog"
  tooltipText="Software Catalog lists calculated software usage results from transferred projects. Use it to search applications, compare categories and types, inspect usage metrics, dependencies, copy/text time and team coverage across the portfolio."
></Header>

{#if catalogMode === 0}
<SectionWrapper
  fullscreen
  fitContent
  {loading}
  loadingTitle="Loading software catalog."
  loadingHint="Please wait while the Software Catalog is prepared."
  helpContext={softwareCatalogHelpContext}
>
  <div class="catalog-main" slot="main">
    <div class="filter-bar">
        <div class="filter-field search-field">
          <SearchNew placeholder="Application name" bind:searchTerm={search} flexGrow />
        </div>
        <div class="filter-field">
          <Select
            placeholder="All categories"
            items={categories.map((category) => ({ value: category, text: category }))}
            bind:selected={selectedCategory}
            onchange={resetPage}
            color="dark"
            labelText="Category"
          />
        </div>
        <div class="filter-field">
          <Select
            placeholder="All types"
            items={types.map((type) => ({ value: type, text: formatType(type) }))}
            bind:selected={selectedType}
            onchange={resetPage}
            color="dark"
            labelText="Type"
          />
        </div>
        <div class="filter-field">         
          <Select
            placeholder="All teams"
            items={teams.map((team) => ({ value: team, text: team }))}
            bind:selected={selectedTeam}
            onchange={resetPage}
            color="dark"
            labelText="Team"
          />
        </div>
        <div class="filter-field">
          <Select
            placeholder="All projects"
            items={projects.map((project) => ({ value: project.id, text: project.name }))}
            bind:selected={selectedProject}
            onchange={loadCatalog}
            color="dark"
            labelText="Project"
          />
        </div>
    </div>

    {#if summary.refresh_required}
      <div class="refresh-notice" role="status">
        This catalog still uses older result data. Run
        <strong>Precompute caches</strong> for the project and transfer it again
        to populate exact users, organization assignments and dependencies.
      </div>
    {/if}

    <section class="catalog-table">
      {#if loading}
        <DataTableSkeleton
          rows={10}
          headers={tableHeaders}
          showToolbar={false}
          showHeader={false}
          size="compact"
        />
      {:else if error}
        <div class="empty-state error">{error}</div>
      {:else if filteredApplications.length === 0}
        <div class="empty-state">
          No calculated software results match the selected filters.
        </div>
      {:else}
        <SoftwareCatalogTable
          headers={tableHeaders}
          rows={filteredApplications}
          {summary}
          bind:page
          bind:pageSize
          getIconUrl={catalogIconUrl}
        />
      {/if}
    </section>

    {#if PAXBOT_ENABLED && paxbotVisible}
      <section class="paxbot_chat_window" aria-label="Paxbot chat">
        <header class="paxbot_chat_header">
          <div>
            <strong>Pax AI</strong>
            <span>Software Catalog Assistant</span>
          </div>
          <div class="paxbot_header_actions">
            <select
              class="paxbot_language_select"
              bind:value={paxbotLanguage}
              aria-label="Pax AI language"
              on:change={handlePaxbotLanguageChange}
            >
              {#each paxbotLanguages as language}
                <option value={language.key}>{language.label}</option>
              {/each}
            </select>
            <button
              type="button"
              class="paxbot_reset_button"
              aria-label="Start new chat"
              title="Start new chat"
              on:click={resetPaxbotChat}
            >
              +
            </button>
            <button
              type="button"
              class="paxbot_close_button"
              aria-label="Close Paxbot"
              title="Close Paxbot"
              on:click={() => (paxbotVisible = false)}
            >
              <img src="/img/assistant/icons/close.svg" alt="" />
            </button>
          </div>
        </header>
        <div class="paxbot_chat_messages" bind:this={paxbotMessagesElement}>
          {#each paxbotChatMessages as message}
            <div
              class:paxbot_user_message={message.role === "user"}
              class="paxbot_message_row"
            >
              {#if message.role === "assistant"}
                <img src="/img/assistant/paxbot_chatsymbol.svg" alt="" />
              {/if}
              <div class="paxbot_message_bubble">
                <span>{message.role === "user" ? "You" : "Paxbot"}</span>
                <p>{message.text}</p>
              </div>
            </div>
          {/each}
          {#if paxbotLoading}
            <div class="paxbot_message_row">
              <img src="/img/assistant/paxbot_chatsymbol.svg" alt="" />
              <div class="paxbot_message_bubble">
                <span>Pax AI</span>
                <p>{paxbotLocale.thinking}</p>
              </div>
            </div>
          {/if}
        </div>
        <div class="paxbot_starter_chips">
          {#each paxbotLocale.starterQuestions as question}
            <button
              type="button"
              disabled={paxbotLoading || paxbotTyping}
              on:click={() => handleStarterQuestion(question)}
            >
              {question}
            </button>
          {/each}
        </div>
        <form
          class="paxbot_chat_input"
          on:submit|preventDefault={() => handlePaxbotQuestion()}
        >
          <textarea
            bind:value={paxbotInput}
            rows="1"
            placeholder={paxbotLoading
              ? paxbotLocale.thinking
              : paxbotTyping
                ? paxbotLocale.replying
                : paxbotLocale.placeholder}
            aria-label="Ask Paxbot"
            on:keydown={handlePaxbotKeydown}
          ></textarea>
          <button
            type="submit"
            aria-label="Send message"
            title="Send message"
            disabled={!paxbotInput.trim() || paxbotLoading || paxbotTyping}
          >
            <img src="/img/assistant/icons/arrow_right.svg" alt="" />
          </button>
        </form>
        <div class="paxbot_disclaimer">
          {paxbotLocale.disclaimer}
        </div>
      </section>
      <img
        class="paxbot_chat_mascot"
        src="/img/assistant/paxbot_portrait.png"
        alt="Paxbot"
      />
    {/if}
  </div>

</SectionWrapper>
{:else}
  <SectionWrapper fullscreen fitContent helpContext={softwareCatalogHelpContext}>
    <div class="ongoing-main" slot="main">
      <div class="ongoing-title">
        <div>
          <span>Continuous monitoring</span>
          <h1>On-going Software Intelligence</h1>
          <p>
            Live software signals will be collected here independently from
            transferred project results.
          </p>
        </div>
      </div>

      <div class="ongoing-empty">
        <span class="ongoing-pulse" aria-hidden="true" />
        <strong>No on-going data source connected</strong>
        <p>
          Once continuous collection is connected, incoming activity, current
          application usage and monitoring signals will appear in this view.
        </p>
      </div>
    </div>

  </SectionWrapper>
{/if}

<style>
  .catalog-main {
    position: relative;
    display: flex;
    width: 100%;
    min-width: 0;
    height: 100%;
    flex-direction: column;
    gap: 1rem;
  }

  h1 {
    margin: 0;
    color: var(--pax-black);
    font-size: 1.5rem;
    font-weight: 700;
    line-height: 1.2;
  }

  .filter-bar {
    display: grid;
    flex: 0 0 auto;
    grid-template-columns: minmax(11rem, 1.35fr) repeat(4, minmax(8rem, 1fr));
    gap: 0.65rem;
    align-items: end;
    padding: 0.65rem;
    border-radius: 0.5rem;
    background: #fff;
  }

  .filter-field {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.25rem;
  }

  .search-field :global(.search) { height: 2.5rem; padding: 0 0.75rem; border-radius: 0.2rem; }
  .search-field :global(.searchicon) { height: 1.25rem; }
  .search-field :global(input) { height: 2.25rem; padding-left: 0.75rem; box-shadow: none !important; }
  .search-field :global(input:focus) { box-shadow: none !important; }

  .catalog-table {
    display: flex;
    min-height: 0;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
    border-radius: 0.5rem;
    background: var(--pax-white);
  }

  .refresh-notice {
    flex: 0 0 auto;
    padding: 0.7rem 0.85rem;
    border-left: 0.25rem solid var(--pax-orange);
    border-radius: 0.25rem;
    background: #fff4ef;
    color: var(--pax-black);
    font-size: 0.75rem;
  }

  .empty-state {
    display: grid;
    min-height: 15rem;
    flex: 1;
    place-items: center;
    color: #6f7681;
  }

  .empty-state.error {
    color: #da1e28;
  }

  .ongoing-main {
    display: flex;
    width: 100%;
    min-width: 0;
    height: 100%;
    flex-direction: column;
    gap: 1rem;
  }

  .ongoing-title {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .ongoing-title > div {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .ongoing-title > div > span {
    color: var(--pax-orange);
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
  }

  .ongoing-title p,
  .ongoing-empty p {
    max-width: 42rem;
    margin: 0;
    color: #6f7681;
    font-size: 0.78rem;
    line-height: 1.45;
  }

  .ongoing-empty {
    display: flex;
    min-height: 22rem;
    flex: 1;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 0.7rem;
    border: 1px dashed #c6c6c6;
    border-radius: 0.5rem;
    background: #fafafa;
    text-align: center;
  }

  .ongoing-empty strong {
    font-size: 1rem;
  }

  .ongoing-pulse {
    width: 1rem;
    height: 1rem;
    border: 0.3rem solid #ffd7c2;
    border-radius: 50%;
    background: var(--pax-orange);
    box-shadow: 0 0 0 0.45rem #fff0e8;
  }

  .paxbot_chat_window {
    position: absolute;
    right: 1rem;
    bottom: 5.85rem;
    z-index: 4;
    display: flex;
    width: min(28rem, calc(100% - 2rem));
    height: min(37rem, calc(100% - 8rem));
    min-height: 18rem;
    flex-direction: column;
    overflow: visible;
    border: 1px solid #e1e4e8;
    border-radius: 0.5rem;
    background: #fff;
    box-shadow: 0 1rem 2.5rem rgba(24, 28, 34, 0.18);
  }

  .paxbot_chat_header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.9rem 1rem;
    background: var(--pax-orange);
    color: white;
  }

  .paxbot_chat_header div {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.15rem;
  }

  .paxbot_header_actions {
    display: flex !important;
    flex: 0 0 auto;
    flex-direction: row !important;
    align-items: center;
    gap: 0.35rem !important;
  }

  .paxbot_language_select {
    height: 1.75rem;
    padding: 0 0.35rem;
    border: 1px solid rgba(255, 255, 255, 0.55);
    border-radius: 0.35rem;
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    font-size: 0.72rem;
    font-weight: 800;
    cursor: pointer;
  }

  .paxbot_language_select option {
    color: var(--pax-black);
  }

  .paxbot_chat_header strong {
    font-size: 0.95rem;
    line-height: 1.2;
  }

  .paxbot_chat_header span {
    font-size: 0.75rem;
    opacity: 0.86;
  }

  .paxbot_reset_button,
  .paxbot_close_button {
    display: flex;
    width: 1.75rem;
    height: 1.75rem;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 0.35rem;
    background: transparent;
    cursor: pointer;
  }

  .paxbot_reset_button {
    border: 1px solid rgba(255, 255, 255, 0.55);
    background: rgba(255, 255, 255, 0.14);
    color: #fff;
    font-size: 1.05rem;
    font-weight: 700;
    line-height: 1;
  }

  .paxbot_reset_button:hover,
  .paxbot_close_button:hover {
    background: rgba(255, 255, 255, 0.16);
  }

  .paxbot_close_button img {
    width: 0.75rem;
    height: 0.75rem;
    filter: brightness(0) invert(1);
  }

  .paxbot_chat_messages {
    display: flex;
    min-height: 0;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem;
    overflow-y: auto;
    background: #f7f8fa;
  }

  .paxbot_chat_messages::-webkit-scrollbar {
    width: 0.45rem;
  }

  .paxbot_chat_messages::-webkit-scrollbar-thumb {
    border-radius: 99px;
    background: #c9ced6;
  }

  .paxbot_message_row {
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .paxbot_message_row > img {
    width: 1rem;
    height: 1rem;
    flex: 0 0 auto;
    margin-top: 0.35rem;
  }

  .paxbot_message_row.paxbot_user_message {
    justify-content: flex-end;
  }

  .paxbot_message_bubble {
    display: flex;
    max-width: 82%;
    flex-direction: column;
    gap: 0.25rem;
    padding: 0.7rem 0.8rem;
    border: 1px solid #e4e6ea;
    border-radius: 0.5rem;
    background: #fff;
    color: #24282d;
    font-size: 0.82rem;
  }

  .paxbot_message_bubble p {
    margin: 0;
    overflow-wrap: anywhere;
    line-height: 1.4;
    white-space: pre-line;
  }

  .paxbot_user_message .paxbot_message_bubble {
    border-color: var(--pax-black);
    background: var(--pax-black);
    color: #fff;
  }

  .paxbot_message_bubble span {
    font-size: 0.72rem;
    font-weight: 700;
    opacity: 0.75;
  }

  .paxbot_starter_chips {
    display: flex;
    max-height: none;
    flex-wrap: wrap;
    gap: 0.4rem;
    padding: 0.5rem 0.75rem 0.85rem;
    overflow: hidden;
    border-top: 1px solid #eef0f2;
    background: #fff;
  }

  .paxbot_starter_chips button {
    width: auto;
    height: 1.75rem;
    min-width: 0;
    max-width: 100%;
    flex: 1 1 calc(50% - 0.25rem);
    padding: 0 0.65rem;
    overflow: hidden;
    border: 1px solid #d7dbe0;
    border-radius: 99px;
    background: #fff;
    color: #3a3f46;
    font-size: 0.72rem;
    font-weight: 700;
    text-overflow: ellipsis;
    white-space: nowrap;
    cursor: pointer;
  }

  .paxbot_starter_chips button:hover:not(:disabled) {
    border-color: var(--pax-orange);
    color: var(--pax-orange);
  }

  .paxbot_starter_chips button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  .paxbot_chat_input {
    display: grid;
    grid-template-columns: 1fr 2.5rem;
    gap: 0.5rem;
    padding: 0.35rem 0.75rem 0.5rem;
    background: #fff;
  }

  .paxbot_chat_input textarea {
    min-height: 2.5rem;
    max-height: 7rem;
    padding: 0.65rem 0.75rem;
    border: 1px solid #d7dbe0;
    border-radius: 0.45rem;
    outline: none;
    font: inherit;
    font-size: 0.82rem;
    resize: vertical;
  }

  .paxbot_chat_input textarea:focus {
    border-color: var(--pax-orange);
  }

  .paxbot_chat_input button {
    display: flex;
    width: 2.5rem;
    height: 2.5rem;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: 0.45rem;
    background: var(--pax-black);
    cursor: pointer;
  }

  .paxbot_chat_input button:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  .paxbot_chat_input button:not(:disabled):hover {
    background: #2a2a2f;
  }

  .paxbot_chat_input button img {
    width: 1rem;
    height: 1rem;
    filter: brightness(0) invert(1);
  }

  .paxbot_disclaimer {
    padding: 0 0.85rem 0.55rem;
    border-bottom-right-radius: 0.5rem;
    border-bottom-left-radius: 0.5rem;
    background: #fff;
    color: #8a929b;
    font-size: 0.68rem;
    line-height: 1.25;
    text-align: center;
  }

  .paxbot_chat_mascot {
    position: absolute;
    right: 1.55rem;
    bottom: -0.65rem;
    z-index: 5;
    width: auto;
    height: 5.6rem;
    pointer-events: none;
  }

  @media (max-width: 1300px) {
    .filter-bar {
      grid-template-columns: repeat(3, minmax(9rem, 1fr));
    }
  }

  @media (max-width: 720px) {
    .paxbot_chat_window {
      right: 0.5rem;
      bottom: 5rem;
      width: calc(100% - 1rem);
      height: min(28rem, calc(100% - 7.25rem));
    }

    .paxbot_chat_mascot {
      right: 1rem;
      bottom: -0.5rem;
      height: 4.8rem;
    }
  }
</style>
